import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  Spinner,
  FlexView,
  ErrorMessage,
  Button,
  SelectDropdown,
  NotFound,
  useModal,
} from '@gsa/afp-component-library';

import { emDashUnicode } from '../../../utilities/constants';
import ToastMessage from '../../../components/Toast/toast';
import {
  ContractLineProvider,
  useContractLine,
} from './provider/contract-line-provider';
import SopContractLineTabs from './contract-line-tabs';
import NonSopContractLineTemplate from './non-sop-line-template/non-sop-contract-line-tab';
import LineHeader from './line-header';

import './helpers/contract-line-template.scss';
import OverlaySpinner from '../../../components/overlay-spinner';
import { pageErrorMessage } from '../../../utilities/error-handling';
import { TABS } from './helpers/constants';
import {
  getOptionalEcList,
  getMinReqSaveObj,
  getOptEqSaveObj,
  getEngineSaveObj,
} from './helpers/ec-helpers';
import {
  getLineDetailTabData,
  getLineTabData,
  getColorsTabData,
  compareScheduleLines,
} from './helpers/line-helpers';
import { getLineVersionFlag, LineVersionFlag } from './helpers/version-helpers';
import ModificationSelectionModal from './modification-selection-modal/modification-selection-modal';
import { isContractSOP } from '../components/contract-helpers';
import ContractLineTemplateActionsPanel from './contract-line-template-actions-panel';
import { ContractLineTemplateBreadcrumbs } from './contract-line-template-breadcrumbs';

const ContractLine = () => {
  const {
    contractLineId,
    contractLine,
    contractLineError,
    isContractLineInitialized,
    loading,
    saving,
    alert,
    setAlert,
    canUpdateContract,
    canVendorUpdateContract,
    canUpdateContractAsOrderingAdmin,

    yearOptions,
    versionOptions,

    saveContractLineMutation,
    lineTabData,
    detailTabData,
    standardECs,
    optionalECs,
    engineData,
    colorsTabData,
    makeAvailableForOrdering,
    contractHeaderId,
  } = useContractLine();

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const navigate = useNavigate();
  const { versionNumber } = useParams();

  const { isOpen, openModal, closeModal } = useModal();

  const canUpdateContractLine =
    canUpdateContract ||
    canVendorUpdateContract ||
    canUpdateContractAsOrderingAdmin;

  const tabIndex = searchParams.get('tabIndex');
  const [lineItems, setLineItems] = useState([]);
  const [lineInput, setLineInput] = useState(null);
  const [modFlag, setModFlag] = useState(undefined);

  const solicitaion = contractLine?.contractHeader?.solicitation;
  const purchaseTypeCode = solicitaion?.purchaseTypeCode;
  const isSOP = isContractSOP(purchaseTypeCode);

  const onYearChange = ({ target: { value } }) => {
    const [headerId, lineId] = value.split('|');
    navigate(`/catalog/contract/${headerId}/contract-line-template/${lineId}`);
  };
  const onVersionChange = ({ target: { value } }) => {
    const historySegment = value ? `/history/${value}` : '';
    navigate(
      `/catalog/contract/${contractHeaderId}/contract-line-template/${contractLineId}${historySegment}`,
    );
  };

  const handleSelectedLineUpdate = (id) => {
    window.location.href = `/catalog/contract/${contractHeaderId}/contract-line-template/${id}?tabIndex=${tabIndex}`;
  };
  useEffect(() => {
    if (contractLine?.contractHeader?.contractLines) {
      const sortedItems = contractLine.contractHeader.contractLines
        .map((item) => ({ value: item.id, label: item.scheduleLine }))
        .sort((a, b) => compareScheduleLines(a.label, b.label));
      setLineItems(sortedItems);
    }
  }, [contractLine]);

  useEffect(() => {
    const { state } = window.history;

    if (state && state.showSuccessMessageOnLoad) {
      setAlert({
        type: 'success',
        message: 'The contract line has been successfully updated.',
      });
      window.history.replaceState(
        { ...state, showSuccessMessageOnLoad: false },
        '',
      );
    }
  }, []);

  const saveContractLineInput = () => {
    setAlert(null);

    // prepare save api payload
    const contractLineInput = { purchaseType: purchaseTypeCode };

    contractLineInput[TABS[0].tabKey] = getLineTabData(
      lineTabData,
      contractLine,
      purchaseTypeCode,
      makeAvailableForOrdering,
    );
    contractLineInput[TABS[1].tabKey] = getLineDetailTabData(
      contractLine,
      detailTabData,
      isSOP,
    );
    contractLineInput[TABS[2].tabKey] = getMinReqSaveObj(
      standardECs,
      contractLineId,
    );
    const ecList = getOptionalEcList(optionalECs);
    contractLineInput[TABS[3].tabKey] = getOptEqSaveObj(
      optionalECs,
      contractLineId,
      ecList,
    );
    contractLineInput[TABS[4].tabKey] = getEngineSaveObj(
      engineData,
      contractLineId,
      ecList,
    );
    contractLineInput[TABS[5].tabKey] = getColorsTabData(
      contractLine.modelColors ?? [],
      colorsTabData,
    );

    const versionFlag = getLineVersionFlag(contractLineInput);

    // no change
    if (versionFlag === LineVersionFlag.NoChange) return;

    const areDatesSetInLine =
      contractLine.orderStartDate && contractLine.orderEndDate;
    const areDatesSetInLineInput =
      contractLineInput.lineTabData?.orderStartDate?.newValue &&
      contractLineInput.lineTabData?.orderEndDate?.newValue;

    const startDateStr =
      lineTabData?.orderStartDate || contractLine.orderStartDate;
    const startDate = startDateStr ? moment.utc(startDateStr).local() : null;
    const endDateStr = lineTabData?.orderEndDate || contractLine.orderEndDate;
    const endDate = endDateStr ? moment.utc(endDateStr).local() : null;

    // prevent user from set any dates to null
    if (contractLineInput.lineTabData?.orderStartDate) {
      const newStartDate =
        contractLineInput.lineTabData?.orderStartDate?.newValue;
      const isValidStartDate = moment(newStartDate).isValid();
      if (!newStartDate || !isValidStartDate) {
        setAlert({
          type: 'error',
          message:
            'The open and closeout date/time must be valid and not empty.',
        });
        return;
      }
    }

    if (contractLineInput.lineTabData?.orderEndDate) {
      const newEndDAte = contractLineInput.lineTabData?.orderEndDate?.newValue;
      const isValidEndDate = moment(newEndDAte).isValid();
      if (!newEndDAte || !isValidEndDate) {
        setAlert({
          type: 'error',
          message:
            'The open and closeout date/time must be valid and not empty.',
        });
        return;
      }
    }

    if (isSOP) {
      if (startDateStr && endDate) {
        if (!endDate.isAfter(startDate)) {
          setAlert({
            type: 'error',
            message:
              'The closeout date/time cannot be prior to the open date/time.',
          });
          return;
        }
      }
    }

    const nonSopFirstTimeSettingDate =
      !isSOP &&
      contractLineInput.lineTabData?.orderStartDate?.newValue &&
      !contractLineInput.lineTabData?.orderStartDate?.oldValue;
    if (
      nonSopFirstTimeSettingDate ||
      (!areDatesSetInLine && !areDatesSetInLineInput)
    ) {
      saveContractLineMutation({
        variables: {
          contractLineId: parseFloat(contractLineId),
          contractLineInput,
          modTag: undefined,
        },
      });
    } else if (versionFlag <= LineVersionFlag.Version) {
      // update or version
      saveContractLineMutation({
        variables: {
          contractLineId: parseFloat(contractLineId),
          contractLineInput,
        },
      });
    } else {
      // mod - optional or required
      setLineInput(contractLineInput);
      setModFlag(versionFlag);
      openModal();
    }
  };

  const onSaveWithMod = (modTag) => {
    saveContractLineMutation({
      variables: {
        contractLineId: parseFloat(contractLineId),
        contractLineInput: lineInput,
        modTag: modTag || undefined,
      },
    });
  };

  if (loading) {
    return (
      <>
        <ContractLineTemplateBreadcrumbs
          scheduleLine={contractLine?.scheduleLine}
        />
        <Spinner />
      </>
    );
  }

  const isNonStandard = purchaseTypeCode === 'N';
  const isMas = purchaseTypeCode === 'M';
  let subHeader = null;
  const showManufacturerNumber =
    (isNonStandard && contractLine?.manufacturerModelNumber) || isMas;

  if (showManufacturerNumber) {
    subHeader = (
      <div className="title-m">
        <span className=" title-m-bold">Manufacturer model number: </span>
        {contractLine?.manufacturerModelNumber || emDashUnicode}
      </div>
    );
  } else {
    subHeader = (
      <>
        <div className="margin-right-2 title-m">
          <span className=" title-m-bold">Body: </span>
          {contractLine?.make?.makeName || emDashUnicode}{' '}
          {contractLine?.model?.modelName || emDashUnicode}
        </div>
        {contractLine?.chassisMake && (
          <div className="title-m">
            <span className=" title-m-bold">Chassis: </span>
            {contractLine?.chassisMake?.makeName || emDashUnicode}{' '}
            {contractLine?.chassisModel?.modelName || emDashUnicode}
          </div>
        )}
      </>
    );
  }

  const hasNoContractLine =
    !contractLine || Object.keys(contractLine).length === 0;

  // Null while the global handler is already showing the system error, so a
  // single failure never produces two banners.
  const loadErrorMessage =
    contractLineError &&
    pageErrorMessage(
      contractLineError,
      contractLineError.message ||
        'An error occurred while loading the contract line data.',
    );

  // A failed load is not a 404 — say why instead of claiming the line is missing.
  if (contractLineError && hasNoContractLine) {
    return (
      <FlexView column>
        <ContractLineTemplateBreadcrumbs
          scheduleLine={contractLine?.scheduleLine}
        />
        {loadErrorMessage && <ErrorMessage>{loadErrorMessage}</ErrorMessage>}
      </FlexView>
    );
  }

  if (isContractLineInitialized && hasNoContractLine) {
    return (
      <FlexView column>
        <ContractLineTemplateBreadcrumbs
          scheduleLine={contractLine?.scheduleLine}
        />
        <NotFound />
      </FlexView>
    );
  }

  return (
    <FlexView column>
      <FlexView>
        {loading && <Spinner data-testid="contract-line-spinner" />}
        {/* a refetch failed while data from an earlier load is still on screen */}
        {loadErrorMessage && <ErrorMessage>{loadErrorMessage}</ErrorMessage>}
      </FlexView>

      {saving && <OverlaySpinner />}

      <ModificationSelectionModal
        isOpen={isOpen}
        contractLineId={Number.parseInt(contractLineId)}
        modFlag={modFlag}
        onSave={onSaveWithMod}
        onClose={closeModal}
      />

      {contractLine && (
        <>
          <ContractLineTemplateBreadcrumbs
            scheduleLine={contractLine?.scheduleLine}
          />
          {alert && (
            <ToastMessage
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
              closable
              className="margin-bottom-2"
            />
          )}

          <FlexView vAlignContent="bottom" grow className="margin-y-2">
            <FlexView grow vAlignContent="center" className="margin-top-1">
              <div className="title-xl">
                Line Item {contractLine?.scheduleLine}
              </div>
              <SelectDropdown
                id="line-dropdown"
                aria-label="line dropdown"
                className="margin-top-0"
                containerClassName="margin-top-0 margin-left-2"
                onChange={(e) => {
                  handleSelectedLineUpdate(e.target.value);
                }}
                options={lineItems || []}
                value={contractLineId || ''}
              />
            </FlexView>
            <FlexView vAlignContent="center" className="margin-bottom-1">
              <SelectDropdown
                id="contract_year_sel"
                aria-label="contract year"
                onChange={onYearChange}
                options={yearOptions}
                value={`${contractHeaderId}|${contractLineId}`}
                className="text-bold font-body-md"
                style={{ width: '13rem' }}
              />
              <span className="padding-left-1" />
              <SelectDropdown
                id="contract_version_sel"
                aria-label="contract version"
                onChange={onVersionChange}
                options={versionOptions}
                value={versionNumber ?? ''}
                className="text-bold font-body-md"
                style={{ width: '8.5rem' }}
              />
            </FlexView>
          </FlexView>

          <FlexView vAlignContent="bottom" grow className="margin-bottom-2">
            <FlexView grow className="margin-top-2">
              {subHeader}
            </FlexView>
            <ContractLineTemplateActionsPanel />
          </FlexView>

          <LineHeader contractLine={contractLine} />

          <FlexView
            className="margin-top-6 margin-bottom-2"
            vAlignContent="center"
          >
            {canUpdateContractLine && (
              <>
                {isSOP ? (
                  <FlexView grow>
                    Upon making edits within any of the tabs below, please be
                    sure to click &quot;<b>Save all changes</b>&quot; before
                    exiting this line item.
                  </FlexView>
                ) : (
                  <FlexView grow>
                    Upon making edits below, please be sure to click &quot;
                    <b>Save all changes</b>&quot; before exiting this line item.
                  </FlexView>
                )}
                <FlexView>
                  <Button
                    label="Save all changes"
                    variant="primary"
                    onClick={saveContractLineInput}
                  />
                </FlexView>
              </>
            )}
          </FlexView>

          {isSOP ? <SopContractLineTabs /> : <NonSopContractLineTemplate />}
        </>
      )}
    </FlexView>
  );
};

const ContractLineTemplate = () => {
  return (
    <ContractLineProvider>
      <ContractLine />
    </ContractLineProvider>
  );
};

export default ContractLineTemplate;
